import { useQuery } from "@tanstack/react-query";

export type ReportType = "allPets" | "interaction";

// Task #99: Report generation timestamp shape
export interface GeneratedAt {
  timestamp: string | null;
  date: string;
  confidence: 'exact' | 'date_only';
}

export interface LocationFreshness {
  locationCode: string;
  locationLabel: string;
  isCurrent: boolean;
  lastReportLocalDate: string | null;
  staleDays: number;
  missingDatesLocal: string[];
  missingCountTotal: number;
  missingCountReturned: number;
  hasNoBaseline: boolean;
  generatedAt?: GeneratedAt | null;
}

interface FreshnessApiResponse {
  serverNowUtc: string;
  expectedDateLocal: string;
  minExpectedReportLocalDate: string;
  dataStartDate: string | null;
  locations: LocationFreshness[];
  summary: {
    anyStale: boolean;
    staleCount: number;
    currentCount: number;
  };
  interaction: {
    locations: LocationFreshness[];
    summary: {
      anyStale: boolean;
      staleCount: number;
      currentCount: number;
    };
  };
}

export interface ReportTypeFreshness {
  locations: LocationFreshness[];
  staleLocations: LocationFreshness[];
  locationsWithGaps: LocationFreshness[];
  staleCount: number;
  gapCount: number;
  isFresh: boolean;
}

export interface StaleLocationEntry extends LocationFreshness {
  sourceReportType: ReportType;
}

export interface StaleStatus {
  staleCount: number;
  gapCount: number;
  isFresh: boolean;
  staleLocations: StaleLocationEntry[];
  locationsWithGaps: StaleLocationEntry[];
  allAffectedLocations: StaleLocationEntry[];
}

export interface ReportFreshnessResult {
  allPets: ReportTypeFreshness;
  interaction: ReportTypeFreshness;
  dataStartDate: string | null;
  getStaleStatus: (reportTypes: ReportType[]) => StaleStatus;
  isLoading: boolean;
  error: Error | null;
}

function computeTypeFreshness(locations: LocationFreshness[] | undefined): ReportTypeFreshness {
  if (!locations) {
    return {
      locations: [],
      staleLocations: [],
      locationsWithGaps: [],
      staleCount: 0,
      gapCount: 0,
      isFresh: true,
    };
  }

  const staleLocations = locations.filter((loc) => !loc.isCurrent);
  const locationsWithGaps = locations.filter((loc) => loc.missingCountTotal > 0);

  return {
    locations,
    staleLocations,
    locationsWithGaps,
    staleCount: staleLocations.length,
    gapCount: locationsWithGaps.length,
    isFresh: staleLocations.length === 0 && locationsWithGaps.length === 0,
  };
}

export function useReportFreshness(): ReportFreshnessResult {
  const { data, isLoading, error } = useQuery<FreshnessApiResponse>({
    queryKey: ["/api/dashboard/freshness"],
    staleTime: 30000,
    refetchInterval: 60000,
  });

  const allPets = computeTypeFreshness(data?.locations);
  const interaction = computeTypeFreshness(data?.interaction?.locations);

  const getStaleStatus = (reportTypes: ReportType[]): StaleStatus => {
    const staleLocations: StaleLocationEntry[] = [];
    const gapLocations: StaleLocationEntry[] = [];
    const staleKeys = new Set<string>();
    const gapKeys = new Set<string>();

    for (const type of reportTypes) {
      const freshness = type === "allPets" ? allPets : interaction;

      for (const loc of freshness.staleLocations) {
        const key = `${type}:${loc.locationCode}`;
        if (!staleKeys.has(key)) {
          staleKeys.add(key);
          staleLocations.push({ ...loc, sourceReportType: type });
        }
      }

      for (const loc of freshness.locationsWithGaps) {
        const key = `${type}:${loc.locationCode}`;
        if (!gapKeys.has(key)) {
          gapKeys.add(key);
          gapLocations.push({ ...loc, sourceReportType: type });
        }
      }
    }

    const staleLocationCodes = new Set(staleLocations.map(l => l.locationCode));
    const gapLocationCodes = new Set(gapLocations.map(l => l.locationCode));

    return {
      staleCount: staleLocationCodes.size,
      gapCount: gapLocationCodes.size,
      isFresh: staleLocations.length === 0 && gapLocations.length === 0,
      staleLocations,
      locationsWithGaps: gapLocations,
      allAffectedLocations: [
        ...staleLocations.filter((l, i, arr) => arr.findIndex(a => a.locationCode === l.locationCode) === i),
        ...gapLocations.filter(l => !staleLocationCodes.has(l.locationCode))
          .filter((l, i, arr) => arr.findIndex(a => a.locationCode === l.locationCode) === i),
      ],
    };
  };

  return {
    allPets,
    interaction,
    dataStartDate: data?.dataStartDate ?? null,
    getStaleStatus,
    isLoading,
    error: error as Error | null,
  };
}
